import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';

export default function ProductModal({ open, onClose, item, refreshHistory }) {
    const [location, setLocation] = useState(item?.location || ''); // current bin
    const [unitsToAdd, setUnitsToAdd] = useState(''); // additional one-unit rows to insert
    const [locations, setLocations] = useState([]); // DB-driven bin list (includes Y/Z)
    const [saving, setSaving] = useState(false);

    // Load the authoritative bin list (same source as the scan page) so Y/Z and any
    // future bins appear and the current location is preserved on edit.
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const token = localStorage.getItem('jwt');
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sflocalinventorylocations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    setLocations(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error('Failed to load locations:', error);
            }
        };
        fetchLocations();
    }, []);

    const handleLocationChange = (e) => setLocation(e.target.value);

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('jwt');

            // 1) Persist a location change first, so any added units clone the NEW bin.
            if (location && location !== item.location) {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/updatelocalinventoryitem`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ id: item.id, location, quantity: 1 }),
                });
                const result = await res.json();
                if (!res.ok || !result.success) {
                    throw new Error(result.message || 'Failed to update location');
                }
            }

            // 2) Add N more one-unit rows (same identifier + location as this row).
            const qty = parseInt(unitsToAdd, 10);
            if (!isNaN(qty) && qty > 0) {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/localinventory/add-units`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ id: item.id, quantity: qty }),
                });
                const result = await res.json();
                if (!res.ok || !result.success) {
                    throw new Error(result.message || 'Failed to add units');
                }
            }

            if (refreshHistory) await refreshHistory();
            onClose();
        } catch (error) {
            console.error('Save failed:', error);
            alert(error.message || 'Save failed — please try again');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setLocation(item?.location || '');
        setUnitsToAdd('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

            <div className="fixed inset-0 z-10 flex items-center justify-center p-4 text-center overflow-y-auto">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <CheckIcon aria-hidden="true" className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                            Edit Item - {item.item_name}
                        </Dialog.Title>

                        <div className="mt-6 space-y-4">
                            {/* FNSKU / identifier */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">FNSKU:</label>
                                <div className="w-2/3 text-sm text-gray-500">{item.fnsku || item.identifier}</div>
                            </div>
                            {/* Seller Sku */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">Seller Sku:</label>
                                <div className="w-2/3 text-sm text-gray-500">{item.seller_sku}</div>
                            </div>
                            {/* ASIN */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">ASIN:</label>
                                <div className="w-2/3 text-sm text-gray-500">{item.asin}</div>
                            </div>
                            {/* Condition */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">Condition:</label>
                                <div className="w-2/3 text-sm text-gray-500">{item.item_condition || item.sku_condition}</div>
                            </div>

                            {/* Additional units to add (semantics B: inserts N more one-unit rows) */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">Additional units to add:</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={unitsToAdd}
                                    onChange={(e) => setUnitsToAdd(e.target.value)}
                                    placeholder="0"
                                    className="w-2/3 mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                />
                            </div>

                            {/* Location (DB-driven) */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">Location:</label>
                                <select
                                    value={location}
                                    onChange={handleLocationChange}
                                    className="w-2/3 mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                >
                                    <option value="">Select Location</option>
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.location}>
                                            {loc.location}{loc.section_type === 'SFP' ? ' (SFP)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Date */}
                            <div className="flex items-center">
                                <label className="w-1/3 text-sm font-medium text-gray-700">Date:</label>
                                <div className="w-2/3 text-sm text-gray-500">{item.created_at}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 sm:flex sm:justify-end">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={saving}
                            className="mr-2 inline-flex justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
